"use client";

import { useEffect, useRef, useState } from "react";
import { LogIn, RefreshCw } from "lucide-react";
import { SidebarInset, SidebarProvider } from "./ui/sidebar";
import { AppSidebar } from "./app-sidebar";
import { SpacetimeGate } from "@/providers/spacetime";
import {
  useCurrentUser,
  useCurrentOrganization,
  usePendingOrgs,
  useMyMemberships,
  usePortalReducers,
} from "@/hooks/spacetime";
import OrganizationCreateDialog from "./organization-create-dialog";
import InviteAcceptDialog from "./invite-accept-dialog";
import LiveChangeToasts from "@/components/live-change-toasts";
import BootstrapSubscriptions from "./bootstrap-subscriptions";
import { setBootGate, useBootReady } from "@/lib/boot-state";
import { useOrgTransitionHeld } from "@/lib/org-transition";
import { login, logout } from "@/app/actions";
import { getCookie } from "@/lib/cookies";
import { showApiErrorToast } from "@/lib/api-error-toast";
import { Button } from "./ui/button";

type AppBootstrapProps = {
  children: React.ReactNode;
};

type Invitation = {
  orgId: string;
  userId: string;
  token: string;
};

/**
 * Preserved from the legacy `app-bootstrap.tsx:69-80` cookie-parsing logic —
 * `userId` here is historically the INVITED PERSON'S EMAIL (see
 * InviteDetailsRow.invitedEmail on the server), not an account id.
 */
function parseInviteParams(raw: string | null): Invitation | null {
  if (!raw) return null;

  const params = new URLSearchParams(raw);
  const orgId = params.get("orgId");
  const userId = params.get("userId");
  const token = params.get("token");

  if (!orgId || !userId || !token) return null;
  return { orgId, userId, token };
}

function BootstrapError({
  error,
  onRetry,
}: {
  error: Error;
  onRetry: () => void;
}) {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background p-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-5 text-center">
        <div className="flex flex-col gap-2">
          <span className="font-headline text-xs font-semibold uppercase text-brand-accent">
            Libum Portal
          </span>
          <h1 className="text-xl font-semibold text-foreground">
            We couldn&apos;t connect to Libum Portal.
          </h1>
          <p className="text-sm text-muted-foreground">
            {error.message ||
              "Your account and organization access are required before the portal can load."}
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          <Button onClick={onRetry}>
            <RefreshCw />
            Retry
          </Button>
          <Button
            variant="secondary"
            onClick={async () => {
              try {
                await logout();
              } catch (logoutError) {
                showApiErrorToast("Auth redirect failed", logoutError);
              }
            }}
          >
            <LogIn />
            Sign in
          </Button>
        </div>
      </div>
    </div>
  );
}

function BootstrappedApp({ children }: { children: React.ReactNode }) {
  const { user, isReady: userReady } = useCurrentUser();
  const { organization, isReady: orgReady } = useCurrentOrganization();
  const { pendingOrgs, isReady: pendingOrgsReady } = usePendingOrgs();
  const { memberships, isReady: membershipsReady } = useMyMemberships();
  const { removeMember } = usePortalReducers();
  const bootReady = useBootReady();
  // A transition still on screen — the delete prompt leaving, or the
  // create-or-join dialog confirming a join request — holds these two back, so
  // nothing cuts over a dialog that is still animating.
  const orgTransitionHeld = useOrgTransitionHeld();
  // Live (non-sticky) view readiness. Used ONLY to suppress the forced
  // dialogs during a post-boot reconnect's empty-cache window (spec §4) —
  // never to unmount content (bootReady is sticky and owns that).
  const viewsLive =
    userReady && orgReady && pendingOrgsReady && membershipsReady;
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [invitationParsed, setInvitationParsed] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  // Ref mirrors isWithdrawing synchronously — guards a rapid double-click
  // that fires before the disabled-button re-render lands.
  const isWithdrawingRef = useRef(false);
  // Guards the sign-in redirect below against firing more than once (e.g. a
  // re-render while login()'s own async work — and eventual navigation — is
  // still in flight).
  const hasRedirectedToLoginRef = useRef(false);
  // Latched "request declined" notice. Rejecting on the org side writes a
  // transient 'rejected' status and then deletes the row, so the signal is
  // gone from the live views almost immediately — this captures it the
  // moment it streams in, keeps the card up after the row vanishes, and
  // only an explicit Dismiss (plain state clear, no reducer call — the org
  // already deleted the row) releases the user back to create/join.
  const [rejectedNotice, setRejectedNotice] = useState<{
    id: string;
    name: string;
    logo: string;
  } | null>(null);
  useEffect(() => {
    if (organization) return;
    const rejected = memberships.find((m) => m.status === "rejected");
    if (!rejected) return;
    const org = pendingOrgs.find((o) => o.id === rejected.organizationId);
    if (!org) return;
    setRejectedNotice({ id: org.id, name: org.name, logo: org.logo });
  }, [memberships, pendingOrgs, organization]);

  // Parsed exactly once — a fresh cookie value never appears mid-session,
  // and re-parsing on every render would fight the dialog's own `onClose`.
  useEffect(() => {
    if (invitationParsed) return;
    setInvitation(parseInviteParams(getCookie("inviteParams")));
    setInvitationParsed(true);
  }, [invitationParsed]);

  // Legacy auto-redirected an unauthenticated visitor straight to OpenAuth
  // login. The spacetime gate accepts anonymous connections (so it can serve
  // the invite-accept flow to signed-out users — see the invitation branch
  // below), which means a signed-out visitor with no invite link would
  // otherwise fall through to the forced org-create dialog instead, whose
  // submit fails ("User not found"). Send them to sign-in instead.
  const isAnonymousWithoutInvite =
    userReady && invitationParsed && !user && !invitation;
  useEffect(() => {
    if (!isAnonymousWithoutInvite) return;
    if (hasRedirectedToLoginRef.current) return;
    hasRedirectedToLoginRef.current = true;
    login().catch((error) => {
      showApiErrorToast("Sign-in redirect failed", error);
    });
  }, [isAnonymousWithoutInvite]);

  // Reports the invite-parse + anonymous-hold decision to the boot store.
  // Deliberately waits for userReady: anonymousHold can only be decided once
  // the my_user subscription has applied, and reporting inviteParsed earlier
  // would let viewsApplied flip the sticky ready before the hold lands
  // (BootstrapSubscriptions' effects flush before this component's).
  useEffect(() => {
    if (!userReady || !invitationParsed) return;
    setBootGate({
      inviteParsed: true,
      anonymousHold: !user && !invitation,
    });
  }, [userReady, invitationParsed, user, invitation]);

  // Pre-ready, the BootOverlay covers the screen; render nothing beneath it
  // until the store says the whole app is ready. Sticky: after first boot
  // this never unmounts content again.
  if (!bootReady) return null;

  // One dialog owns every "you have no organization" state — create, join,
  // waiting on approval, declined — so the caller morphs between them instead
  // of one blocking card replacing another.
  const isOrgSetupForced =
    viewsLive && !!user && !organization && !invitation && !orgTransitionHeld;
  // The latched declined notice wins over the (possibly already deleted)
  // pending row.
  const declinedOrg = rejectedNotice && !organization ? rejectedNotice : null;
  const pendingOrg =
    isOrgSetupForced && !declinedOrg && pendingOrgs.length > 0
      ? pendingOrgs[0]
      : null;

  const handleWithdraw = async () => {
    if (!pendingOrg || isWithdrawingRef.current) return;
    const membership = memberships.find(
      (m) => m.organizationId === pendingOrg.id && m.status === "requested",
    );
    if (!membership) return;

    isWithdrawingRef.current = true;
    setIsWithdrawing(true);
    try {
      await removeMember({
        id: membership.id,
        userId: membership.userId,
        organizationId: membership.organizationId,
      });
    } catch (error) {
      showApiErrorToast("Failed to withdraw request", error);
    } finally {
      isWithdrawingRef.current = false;
      setIsWithdrawing(false);
    }
  };

  return (
    <>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <div className="lb-scrollbar-overlay flex h-svh max-h-svh w-full min-w-0 flex-1 flex-col overflow-x-hidden bg-background md:h-[calc(100svh-theme(spacing.4))] md:max-h-[calc(100svh-theme(spacing.4))] md:overflow-hidden md:rounded-xl">
            <LiveChangeToasts />
            {children}
          </div>
        </SidebarInset>
      </SidebarProvider>
      {/* Prompts when the user has no organization: create/join, and the
          waiting and declined states of a join request */}
      <OrganizationCreateDialog
        controlledOpen={isOrgSetupForced}
        isForced
        pendingRequest={pendingOrg}
        declinedRequest={declinedOrg}
        onWithdrawRequest={handleWithdraw}
        onDismissDeclined={() => setRejectedNotice(null)}
        isWithdrawingRequest={isWithdrawing}
      />
      {/* Prompts when an invitation has been opened */}
      {invitation && (
        <InviteAcceptDialog
          controlledOpen
          invitation={invitation}
          onClose={() => setInvitation(null)}
        />
      )}
    </>
  );
}

export default function AppBootstrap({ children }: AppBootstrapProps) {
  return (
    <SpacetimeGate
      errorFallback={(err, retry) => (
        <BootstrapError error={err} onRetry={retry} />
      )}
    >
      <BootstrapSubscriptions />
      <BootstrappedApp>{children}</BootstrappedApp>
    </SpacetimeGate>
  );
}
